import {
  RECEIVER_ADDRESS1,
  RECEIVER_ADDRESS2,
  RECEIVER_CITY,
  RECEIVER_COMPANY_NAME,
  RECEIVER_COUNTRY,
  RECEIVER_EMAIL,
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
  RECEIVER_STATE,
  RECEIVER_TAX_NUMBER,
  RECEIVER_ZIP,
} from '../constants/shipping';
import { IAddressInfo, IContactInfo, IFixedAddress } from '../types/api';
import { ISelectorValue } from '../types/shell';
import { IAddressSuggestion, IFormattedAddress } from '../types/shipping';

export const getAddressHeading = ({ label, company_name }: IFixedAddress): string | undefined => label || company_name;

export const getAddressBodyLines = ({
  label,
  company_name,
  address1,
  address2,
  city,
  state,
  zip,
  country,
}: IFixedAddress): string[] => {
  const cityStateZip = [city, state, zip].filter(Boolean).join(', ');

  // Company name leads the address block, then the street/city/country lines — but skip it when
  // it's already the heading (no label), so it isn't shown twice.
  const company = label ? company_name : undefined;

  return [company, address1, address2, cityStateZip, country].filter((line): line is string => !!line);
};

export const matchesAddressQuery = (address: IFixedAddress, query: string): boolean => {
  if (!query) {
    return true;
  }
  // Match label + company + every address line, regardless of which is the visible heading.
  const haystack = [address.label, address.company_name, ...getAddressBodyLines(address)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
};

export const formatApiAddress = (address: IAddressSuggestion): IFormattedAddress | null => {
  const { city, secondary, state, streetLine, zipcode } = address;
  if (city && state && streetLine && zipcode) {
    return {
      [RECEIVER_CITY]: city,
      [RECEIVER_ADDRESS1]: streetLine,
      [RECEIVER_ADDRESS2]: secondary || '',
      [RECEIVER_STATE]: state,
      [RECEIVER_ZIP]: zipcode,
    };
  }

  return null;
};

export const formatSuggestionLabel = ({ streetLine, secondary, city, state, zipcode }: IAddressSuggestion): string =>
  `${streetLine}, ${secondary ? `${secondary}, ` : ''}${city}, ${state} ${zipcode}`;

export const EMPTY_ADDRESS: IAddressInfo = {
  [RECEIVER_ADDRESS1]: '',
  [RECEIVER_ADDRESS2]: '',
  [RECEIVER_STATE]: '',
  [RECEIVER_COUNTRY]: '',
  [RECEIVER_CITY]: '',
  [RECEIVER_ZIP]: '',
  [RECEIVER_COMPANY_NAME]: '',
};

export const EMPTY_CONTACT: IContactInfo = {
  [RECEIVER_FIRST_NAME]: '',
  [RECEIVER_LAST_NAME]: '',
  [RECEIVER_EMAIL]: '',
  [RECEIVER_PHONE]: '',
  [RECEIVER_TAX_NUMBER]: '',
};

// Common names that browsers and address books use but that the ISO labels in countryList don't
// reduce to, e.g. "United States" vs "United States of America (the)".
const COUNTRY_ALIASES: Record<string, string> = {
  'united states': 'US',
  usa: 'US',
  uk: 'GB',
  'united kingdom': 'GB',
  'great britain': 'GB',
  'south korea': 'KR',
  'north korea': 'KP',
  russia: 'RU',
  vietnam: 'VN',
  laos: 'LA',
  syria: 'SY',
  'north macedonia': 'MK',
  'czech republic': 'CZ',
  uae: 'AE',
  brunei: 'BN',
};

// "Bolivia (Plurinational State of)" -> "bolivia", "Tanzania, United Republic of" -> "tanzania".
const normalizeCountryName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\(.*?\)|\[.*?\]/g, ' ')
    .split(',')[0]
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Resolves free text, such as a browser-autofilled country name or code, to one of the given
// options. Returns null unless the text names exactly one of them.
export const findCountryOption = <T extends ISelectorValue>(text: string, options: T[]): T | null => {
  const code = text.trim().toUpperCase();
  const byCode = options.find((option) => option.value === code);

  if (byCode) {
    return byCode;
  }

  const name = normalizeCountryName(text);

  if (!name) {
    return null;
  }

  const byName = options.filter((option) => normalizeCountryName(option.label) === name);

  if (byName.length) {
    return byName.length === 1 ? byName[0] : null;
  }

  return options.find((option) => option.value === COUNTRY_ALIASES[name]) || null;
};
